import React, { useMemo } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { NEMONTO_TEMPLATE_MAP } from "./templates";
import PostDetailPage from "./post-detail-page";
import HomeAppPage from "./home-app-page";
import CreateRecipePage from "./create-recipe-page";

const pageFrameStyle = {
  width: "100%",
  height: "100vh",
  border: 0,
  display: "block",
};

const FLOW_SCRIPT = String.raw`
  <script>
    (function () {
      const rules = [
        { match: /sign in|log in/, to: "/auth/sign-in" },
        { match: /sign up|join now|request invite|save my seat|start sharing/, to: "/auth/sign-up" },
        { match: /host a challenge/, to: "/pages/challenge-submit" },
        { match: /enter challenge|enter this challenge|enter now/, to: "/pages/challenge-entries" },
        { match: /new recipe|create recipe|post recipe/, to: "/pages/post-submit" },
        { match: /view recipe|recipe detail|post detail/, to: "/pages/post-detail" },
        { match: /back to recipe/, to: "/pages/post-detail" },
        { match: /back to your challenges/, to: "/pages/challenges-listing" },
        { match: /made from/, to: "/pages/made-it-for-recipe" },
        { match: /home|back to feed/, to: "/pages/home" },
        { match: /\bexplore\b/, to: "/pages/challenges-listing" },
        { match: /\bchallenges?\b/, to: "/pages/challenges-listing" },
        { match: /made it/, to: "/pages/made-it-hub" },
        { match: /saved/, to: "/pages/profile-other" },
        { match: /message|chat|inbox/, to: "/pages/post-detail" },
        { match: /profile/, to: "/pages/profile" },
        { match: /all templates|templates/, to: "/pages" }
      ];

      const profileOtherSelectors = [
        ".author-row",
        ".author-name",
        ".author-avatar",
        ".post-top",
        ".post-avatar",
        ".post-who",
        ".person-row",
        ".pr-avatar",
        ".pr-name",
        ".reply-top",
        ".reply-avatar",
        ".reply-name",
        ".sc-top",
        ".sc-avatar",
        ".sc-name",
        ".cc-avatar",
        ".cc-name",
        ".cc-handle",
        ".id-name-row",
        ".avatar-xl",
        ".also-follow",
        ".af-avatar",
        ".af-name",
        ".top-recipe-card",
        ".mutual-card",
        ".qwho"
      ];

      const profileActionSelectors = [
        ".follow-btn",
        ".follow-pill",
        ".cc-follow",
        ".pr-action",
        ".sc-follow",
        ".af-follow",
        ".msg-btn",
        ".more-btn"
      ];

      const goTop = (path) => {
        if (!path) return;
        if (window.top) {
          window.top.location.href = path;
          return;
        }
        window.location.href = path;
      };

      const performLogout = () => {
        try {
          localStorage.removeItem("uzoneAuthToken");
          localStorage.removeItem("user_email");
        } catch (error) {
          // Ignore localStorage access errors in restricted contexts.
        }

        try {
          if (window.top && window.top.localStorage) {
            window.top.localStorage.removeItem("uzoneAuthToken");
            window.top.localStorage.removeItem("user_email");
          }
        } catch (error) {
          // Ignore top-frame localStorage access errors.
        }

        goTop("/");
      };

      let logoutMenu = null;

      const hideLogoutMenu = () => {
        if (logoutMenu) {
          logoutMenu.remove();
          logoutMenu = null;
        }
      };

      const showLogoutMenu = (anchorEl) => {
        hideLogoutMenu();

        const menu = document.createElement("div");
        menu.style.position = "fixed";
        menu.style.zIndex = "999999";
        menu.style.background = "#ffffff";
        menu.style.border = "1px solid rgba(44,44,42,0.14)";
        menu.style.borderRadius = "10px";
        menu.style.boxShadow = "0 12px 24px rgba(44,28,18,0.18)";
        menu.style.padding = "6px";

        const button = document.createElement("button");
        button.type = "button";
        button.textContent = "Logout";
        button.style.border = "none";
        button.style.background = "#fff";
        button.style.color = "#b42318";
        button.style.fontWeight = "700";
        button.style.padding = "8px 12px";
        button.style.borderRadius = "8px";
        button.style.cursor = "pointer";
        button.addEventListener("click", (event) => {
          event.preventDefault();
          event.stopPropagation();
          performLogout();
        });

        menu.appendChild(button);
        document.body.appendChild(menu);
        logoutMenu = menu;

        const rect = anchorEl.getBoundingClientRect();
        menu.style.left = Math.max(10, rect.left - 74) + "px";
        menu.style.top = (rect.top - 44) + "px";
      };

      const textToPath = (text) => {
        const normalized = (text || "").replace(/\s+/g, " ").trim().toLowerCase();
        if (!normalized) return null;

        for (const rule of rules) {
          if (rule.match.test(normalized)) {
            return rule.to;
          }
        }
        return null;
      };

      const elementToPath = (target) => {
        if (!target) return null;

        if (target.closest(".close-btn") || target.closest("button[aria-label='Close']")) {
          return "/pages/home";
        }

        if (profileActionSelectors.some((selector) => target.closest(selector))) {
          return null;
        }

        if (
          target.closest(
            ".post-card,.post-thumb,.post-title,.media-box,.mc-media,.reply-media,.related-card,.grid-tile,.top-recipe-card,.rc-thumb"
          )
        ) {
          return "/pages/post-detail";
        }

        if (target.closest(".source-chip")) {
          return "/pages/made-it-for-recipe";
        }

        if (profileOtherSelectors.some((selector) => target.closest(selector))) {
          return "/pages/profile-other";
        }

        return null;
      };

      document.querySelectorAll('a[href^="/"]').forEach((a) => {
        a.setAttribute("target", "_top");
      });

      document.addEventListener("click", (event) => {
        const target = event.target.closest("a,button,[role='button'],.pm-more,.post-card,.grid-tile,.ch-card,.ch-enter-btn,.source-chip,.back-row,.post-btn,.cm-btn,.featured-cta,.host-cta-btn,.enter-cta-btn,.pv-enter-btn,.rp-view-btn,.cart-btn,.empty-cta,.store-btn,.author-row,.author-name,.author-avatar,.post-top,.post-avatar,.post-who,.person-row,.pr-avatar,.pr-name,.reply-top,.reply-avatar,.reply-name,.sc-top,.sc-avatar,.sc-name,.cc-avatar,.cc-name,.cc-handle,.id-name-row,.avatar-xl,.also-follow,.af-avatar,.af-name,.top-recipe-card,.mutual-card,.qwho");
        if (!target) {
          hideLogoutMenu();
          return;
        }

        if (target.closest(".pm-more")) {
          event.preventDefault();
          event.stopPropagation();
          if (logoutMenu) {
            hideLogoutMenu();
          } else {
            showLogoutMenu(target.closest(".pm-more"));
          }
          return;
        }

        hideLogoutMenu();

        const anchor = target.closest("a");
        if (anchor) {
          const href = anchor.getAttribute("href") || "";
          if (/profile-other|nemonto-profile-other\.html/i.test(href)) {
            event.preventDefault();
            goTop("/pages/profile-other");
            return;
          }
          if (/nemonto-profile\.html/i.test(href)) {
            event.preventDefault();
            goTop("/pages/profile");
            return;
          }
          if (href.startsWith("/")) {
            event.preventDefault();
            goTop(href);
            return;
          }
          if (href && href !== "#") {
            return;
          }
        }

        const explicitPath = elementToPath(target);
        if (explicitPath) {
          event.preventDefault();
          goTop(explicitPath);
          return;
        }

        const path = textToPath(target.textContent || "");
        if (path) {
          event.preventDefault();
          goTop(path);
        }
      });
    })();
  </script>
`;

const injectFlowScript = (html) => {
  if (!html) {
    return html;
  }

  if (html.includes("window.top.location.href") || html.includes("__nemonto_flow_script__")) {
    return html;
  }

  const taggedScript = FLOW_SCRIPT.replace("(function () {", "(function () { window.__nemonto_flow_script__ = true;");
  if (/<\/body>/i.test(html)) {
    return html.replace(/<\/body>/i, `${taggedScript}</body>`);
  }

  return `${html}${taggedScript}`;
};

// These templates are now rendered inside the real app Sidebar (Default
// layout), but each mockup was designed as a standalone screen with its own
// baked-in "<aside class='sidebar'>" + a 3-column ".app" grid reserving
// space for it. Hide that duplicate sidebar and reclaim its grid column.
// `:has()` self-guards this so it's a no-op on templates (post-submit,
// challenge-submit) that never had an internal sidebar to begin with, and
// `!important` wins over the template's own un-important breakpoint rules
// regardless of the exact px values those happen to use.
const DUPLICATE_SIDEBAR_FIX_STYLE = `
  <style id="__nemonto_hide_duplicate_sidebar__">
    .sidebar { display: none !important; }
    .app:has(> .sidebar) { grid-template-columns: minmax(0,1fr) 300px !important; }
    @media (max-width: 900px) {
      .app:has(> .sidebar) { grid-template-columns: minmax(0,1fr) !important; }
    }
  </style>
`;

const injectDuplicateSidebarFix = (html) => {
  if (!html || html.includes("__nemonto_hide_duplicate_sidebar__")) {
    return html;
  }

  if (/<\/head>/i.test(html)) {
    return html.replace(/<\/head>/i, `${DUPLICATE_SIDEBAR_FIX_STYLE}</head>`);
  }

  return `${DUPLICATE_SIDEBAR_FIX_STYLE}${html}`;
};

const NemontoTemplatePage = () => {
  const { pageKey } = useParams();
  const templateEntry = NEMONTO_TEMPLATE_MAP[pageKey || ""];
  const srcDoc = useMemo(
    () => injectFlowScript(injectDuplicateSidebarFix(templateEntry?.html || "")),
    [templateEntry]
  );
  const authToken = localStorage.getItem("uzoneAuthToken");

  const pageLabel = useMemo(() => pageKey || "unknown", [pageKey]);

  if (!authToken) {
    return <Navigate to="/auth/sign-in" replace />;
  }

  if (pageKey === "post-detail") {
    return <PostDetailPage />;
  }

  if (pageKey === "home") {
    return <HomeAppPage />;
  }

  if (pageKey === "post-submit") {
    return <CreateRecipePage />;
  }

  if (!templateEntry) {
    return (
      <section style={{ padding: "32px", fontFamily: "system-ui, sans-serif" }}>
        <h1>Template not found</h1>
        <p>Choose a valid page from the Nemonto templates list.</p>
        <Link to="/pages">Go to templates list</Link>
      </section>
    );
  }

  return (
    <iframe
      title={`nemonto-template-${pageLabel}`}
      srcDoc={srcDoc}
      style={pageFrameStyle}
      sandbox="allow-same-origin allow-scripts allow-forms allow-modals allow-top-navigation-by-user-activation"
    />
  );
};

export default NemontoTemplatePage;